/**
 * useTechnicianFilter — technician visibility filter for the route preview board.
 *
 * Owns the selected-technician state, persists the choice per device + tenant in
 * localStorage, and applies an adaptive default once routes are known: small
 * boards show everyone, large boards default to just the first technician so the
 * map doesn't render every route's live driving directions at once.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { BoardRoute } from '../utils/routeBoardMapping';

/**
 * Boards with more technicians than this default to showing only the first
 * technician. Smaller boards show everyone by default.
 */
const MAX_DEFAULT_ALL_TECHS = 5;

export interface TechnicianFilter {
  /** null = "all technicians"; otherwise the explicitly selected ids. */
  selectedTechnicianIds: string[] | null;
  /** Update + persist the selection (null clears the filter to "all"). */
  handleTechFilterChange: (ids: string[] | null) => void;
}

export function useTechnicianFilter(
  tenantId: string | null | undefined,
  boardRoutes: BoardRoute[]
): TechnicianFilter {
  const techFilterKey = tenantId ? `vehix_route_preview_techs_${tenantId}` : null;
  const [selectedTechnicianIds, setSelectedTechnicianIds] = useState<string[] | null>(null);
  const filterInitialized = useRef(false);

  // Initialize once: honor a stored preference immediately, otherwise wait for
  // routes to load and pick the adaptive default.
  useEffect(() => {
    if (!techFilterKey || filterInitialized.current) return;

    let raw: string | null = null;
    try {
      raw = localStorage.getItem(techFilterKey);
    } catch {
      // Storage unavailable (private mode) — fall through to adaptive default
    }
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        setSelectedTechnicianIds(parsed === 'ALL' ? null : parsed);
      } catch {
        // Ignore corrupt persisted value
      }
      filterInitialized.current = true;
      return;
    }

    if (boardRoutes.length === 0) return; // wait for routes before defaulting

    filterInitialized.current = true;
    const techIds = Array.from(
      new Map(boardRoutes.map(b => [b.technician_id, b.technicianName])).entries()
    )
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id]) => id);
    const firstTech = techIds[0];
    setSelectedTechnicianIds(
      techIds.length > MAX_DEFAULT_ALL_TECHS && firstTech ? [firstTech] : null
    );
  }, [techFilterKey, boardRoutes]);

  const handleTechFilterChange = useCallback(
    (ids: string[] | null) => {
      setSelectedTechnicianIds(ids);
      if (techFilterKey) {
        try {
          localStorage.setItem(techFilterKey, JSON.stringify(ids === null ? 'ALL' : ids));
        } catch {
          // Storage unavailable (private mode) — selection still applies this session
        }
      }
    },
    [techFilterKey]
  );

  return { selectedTechnicianIds, handleTechFilterChange };
}

export default useTechnicianFilter;
